import { TypeIcon } from "../common/TypeIcon";
import { useState, useEffect, useMemo, useRef, useCallback, type KeyboardEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs, getCountFromServer, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { useAnnounceLogged, useCloseSheet } from "../../contexts/LogSheetContext";
import { LogBestGuess } from "./LogBestGuess";
import { pickBestGuess } from "../../utils/bestGuess";
import { NearbyEntities, NearbySkeleton } from "./NearbyEntities";
import { LogActivitySearch } from "./LogActivitySearch";
import { buildNestedSegmentOptions } from "../../utils/segmentTree";
import {
  createInteractionAt,
  isContainerEntityType,
  buildTripContextSearch,
  startHotelVisit,
} from "../../utils/activityLogging";
import { useHotelStays } from "../../hooks/useHotelStays";
import { stayHotel, stayHotelName } from "../../utils/hotelStays";
import {
  findDefaultParentInteractionId,
  parentCandidateActivities,
} from "../../utils/activityTree";
import { useEntityMap } from "../../hooks/useEntityMap";
import { useActiveAsOf } from "../../hooks/useActiveAsOf";
import { useEntityHierarchy } from "../../hooks/useEntityHierarchy";
import { useNearbyEntities, type NearbyResult } from "../../hooks/useNearbyEntities";
import { partitionByActive } from "../../utils/entityActive";
import { travelPicks } from "../../utils/travelPicks";
import {
  CUSTOM_INTERACTION_TYPES,
  getInteractionEntityType,
  isCruiseSegment,
  isCruiseDay,
  isTravelSegment,
} from "../../types";
import { storedEntityTypes, withCurrentType } from "../../types";
import type { Entity, EntityType, EventOption, Interaction } from "../../types";
import type { SegmentWithId } from "../../hooks/useSegments";
import { deviceZone, entityTypeLabel, isActivityActive, segmentDayLookup } from "../../utils/tripDisplay";
import "./LogActivityPicker.css";

// CaptainsLog-style quick activity types for cruise segments. Each creates an
// entity-less interaction with `customResponses.activityType` set so the right
// type-specific editor surfaces on the detail page (dining/lounge → orders,
// trivia → trivia editor). An optional `entityType` also seeds the matching
// event defaults (e.g. dining → Food & drink's Seated / Order Placed steps).
// The shortcuts keep their everyday names; underneath, a lounge is Food &
// drink and a show, excursion or trivia round is an Experience.
const CRUISE_ACTIVITY_TYPES: { key: string; label: string; icon: string; defaultName: string | null; entityType?: EntityType }[] = [
  { key: "ship-ops", label: "Ship Ops", icon: "🛳️", defaultName: "Ship Operations" },
  { key: "excursion", label: "Excursion", icon: "🥾", defaultName: "Excursion", entityType: "EXPERIENCE" },
  { key: "dining", label: "Dining", icon: "🍽️", defaultName: "Dining", entityType: "RESTAURANT" },
  { key: "show", label: "Show", icon: "🎭", defaultName: "Show", entityType: "EXPERIENCE" },
  { key: "lounge", label: "Lounge", icon: "🍸", defaultName: "Lounge", entityType: "RESTAURANT" },
  { key: "trivia", label: "Trivia", icon: "❓", defaultName: "Trivia", entityType: "EXPERIENCE" },
  { key: "custom", label: "Custom", icon: "✏️", defaultName: null },
];

interface SuggestedEntity {
  id: string;
  displayName: string;
  type: EntityType;
  imageUrl?: string | null;
}

/** Tile labels for the type grid: three tiles fit a phone only if the longest
 *  one doesn't wrap mid-word. Everywhere else the full label is used. */
function customTypeTileLabel(t: EntityType): string {
  return t === "TRANSPORTATION" ? "Transport" : entityTypeLabel(t);
}

/**
 * The custom flow's type choice. `null` is "not chosen yet" (still on the type
 * step); `""` is a deliberate "no type" — just a name — which skips to naming.
 */
type CustomTypeChoice = EntityType | "" | null;

interface LogActivityPickerProps {
  tripId: string;
  segments: SegmentWithId[];
  defaultSegmentId: string | null;
  /** Entity IDs used to seed the suggestion list. Each is walked up the
   *  parent chain so ancestors (land → park → resort → destination) appear too. */
  seedEntityIds: (string | null | undefined)[];
  /** The trip's activities — the places already logged that this one can go
   *  inside of. Omitted (or empty) and the sheet just files under a segment,
   *  exactly as it did before nesting existed. */
  interactions?: (Interaction & { id: string })[];
  /**
   * Force what the new activity is logged inside of, skipping the guess — set
   * when the sheet was opened from a place's own page ("log an activity here").
   */
  parentInteractionId?: string | null;
}

export function LogActivityPicker({
  tripId,
  segments,
  defaultSegmentId,
  seedEntityIds,
  interactions = [],
  parentInteractionId,
}: LogActivityPickerProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<SuggestedEntity[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  // Full entity docs (not just the SuggestedEntity subset) so the active-days
  // filter below can read their inherited dates.
  const [shipEntities, setShipEntities] = useState<(Entity & { id: string })[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ===== The sheet's three views =====
  // "pick" is the default: search at the top, suggestions beneath. Typing a
  // query swaps the suggestions for inline results. "custom" is its own
  // stepped flow (type → name → create) rather than a row of fields at the
  // bottom of the sheet, where the type picker used to hide below the fold.
  const [mode, setMode] = useState<"pick" | "custom">("pick");
  const [search, setSearch] = useState("");
  const [customType, setCustomType] = useState<CustomTypeChoice>(null);
  const [customName, setCustomName] = useState("");
  const [customLocation, setCustomLocation] = useState("");
  // Where the activity files is shown as a one-line summary, and only becomes
  // a pair of selects once tapped — the guess is right nearly every time.
  const [contextOpen, setContextOpen] = useState(false);

  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(defaultSegmentId);
  // Keep the selection in sync with the default until the user picks one — the
  // default often arrives after mount (segments/interactions load async), so a
  // one-time useState initializer would otherwise stick on the wrong value.
  const userPickedSegment = useRef(false);
  useEffect(() => {
    if (!userPickedSegment.current) setSelectedSegmentId(defaultSegmentId);
  }, [defaultSegmentId]);

  // ===== What this activity is logged inside of =====
  // The places already logged under this segment that you might still be in —
  // the park you checked into, the airport you're waiting in. See
  // `utils/activityTree`.
  const parentLocked = parentInteractionId !== undefined;
  const parentCandidates = useMemo(
    () =>
      parentLocked
        ? []
        : parentCandidateActivities(interactions, selectedSegmentId, segmentDayLookup(segments)),
    [parentLocked, interactions, selectedSegmentId, segments],
  );
  // Opened from a place's own page: that place holds this activity, and the
  // segment comes with it rather than being asked about again.
  const lockedParent = parentLocked
    ? interactions.find((i) => i.id === parentInteractionId) ?? null
    : null;
  // Names (and types) for the candidates, so the picker can label them and tell
  // a park from a ride.
  const candidateEntityIds = useMemo(
    () =>
      [...parentCandidates.map((a) => a.entityId), lockedParent?.entityId].filter(
        (id): id is string => id != null,
      ),
    [parentCandidates, lockedParent?.entityId],
  );
  const { entityMap: parentEntityMap, entityDataMap: parentEntityDataMap } =
    useEntityMap(candidateEntityIds);

  const [selectedParentId, setSelectedParentId] = useState<string | null>(
    parentInteractionId ?? null,
  );
  const userPickedParent = useRef(false);
  const suggestedParentId = useMemo(() => {
    if (parentLocked) return null;
    return findDefaultParentInteractionId(
      interactions,
      selectedSegmentId,
      (a) =>
        getInteractionEntityType(
          a.entityId ? { type: parentEntityDataMap[a.entityId]?.type ?? undefined } : null,
          a.customResponses,
        ),
      segmentDayLookup(segments),
    );
  }, [parentLocked, interactions, selectedSegmentId, parentEntityDataMap, segments]);
  // Follow the guess until the user overrides it. Changing segment starts the
  // question over — a place in the old segment can't hold this activity.
  useEffect(() => {
    if (parentLocked) return;
    userPickedParent.current = false;
  }, [parentLocked, selectedSegmentId]);
  useEffect(() => {
    if (parentLocked) {
      setSelectedParentId(parentInteractionId ?? null);
      return;
    }
    if (!userPickedParent.current) setSelectedParentId(suggestedParentId);
  }, [parentLocked, parentInteractionId, suggestedParentId]);

  const activityLabel = (a: Interaction & { id: string }): string =>
    a.name || (a.entityId && parentEntityMap[a.entityId]) || "Unnamed activity";
  const selectedParent = parentCandidates.find((a) => a.id === selectedParentId) ?? null;
  const selectedSegment = segments.find((s) => s.id === selectedSegmentId) ?? null;
  const effectiveParent = selectedParent ?? lockedParent;

  // One answer to "what counts as a place right now", shared by the search
  // results, the nearby strip and the ship's venue list below — so they can't
  // disagree about whether last year's haunted house exists.
  const activeDuring = useActiveAsOf({ tripId, segmentId: selectedSegmentId, segments });

  // ===== Back at the hotel =====
  // Mid-stay and out of the hotel, the hotel leads the Near You strip when
  // you're close to it, else the Recent one, tagged "Back": one tap logs the
  // stay's next visit, Arrived now. No row of its own — it's a few taps a day
  // against dozens of other logs. See `utils/hotelStays`.
  const stayEntityIds = useMemo(
    () => [...new Set(interactions.map((i) => i.entityId).filter((id): id is string => !!id))],
    [interactions],
  );
  const { entityDataMap: tripEntityData } = useEntityMap(stayEntityIds);
  const stayOnDay = useMemo(() => segmentDayLookup(segments), [segments]);
  const { returnTo: returnStay } = useHotelStays(interactions, tripEntityData, stayOnDay);
  const returnStayHere = parentLocked ? null : returnStay;
  const returnHotel = returnStayHere ? stayHotel(returnStayHere, tripEntityData) : undefined;
  const returnHotelName = returnStayHere ? stayHotelName(returnStayHere, returnHotel) : "Hotel";
  const [returnNear, setReturnNear] = useState(false);
  const handleBackAtHotel = useCallback(async () => {
    if (!user || creating || !returnStayHere) return;
    setCreating(true);
    setError(null);
    try {
      const id = await startHotelVisit({
        userId: user.uid,
        tripId,
        segmentId: selectedSegmentId,
        hotel: returnStayHere,
      });
      navigate(`/trips/${tripId}/interactions/${id}`);
    } catch {
      setError("Failed to create activity. Please try again.");
      setCreating(false);
    }
  }, [user, creating, returnStayHere, tripId, selectedSegmentId, navigate]);
  const nearbyReturn = useMemo(
    // Only a hotel place has a location; a custom one takes the Recent tile.
    () =>
      returnStayHere?.entityId ? { entityId: returnStayHere.entityId, onReturn: handleBackAtHotel } : undefined,
    [returnStayHere, handleBackAtHotel],
  );
  const recentBack = returnStayHere && !returnNear ? returnStayHere.key : null;

  // The best guess: what "Near you" found closest, unless you're already
  // logging it (or walking back into your hotel, which has its own card).
  const [nearbyList, setNearbyList] = useState<NearbyResult<Entity & { id: string }>[]>([]);
  const openEntityIds = useMemo(
    () =>
      new Set(
        interactions
          .filter((i) => i.entityId && isActivityActive(i.events))
          .map((i) => i.entityId as string),
      ),
    [interactions],
  );
  const bestGuess = useMemo(() => {
    if (returnNear) return null;
    const guess = pickBestGuess(nearbyList, openEntityIds);
    return guess && guess.entity.id !== returnStayHere?.entityId ? guess : null;
  }, [nearbyList, openEntityIds, returnNear, returnStayHere]);
  const closeSheet = useCloseSheet();
  const announceLogged = useAnnounceLogged();

  // Resolve whether the selected segment is part of a cruise, and which ship
  // entity (if any) that cruise uses.
  const { isCruise, shipEntityId } = useMemo(() => {
    const seg = segments.find((s) => s.id === selectedSegmentId);
    if (!seg) return { isCruise: false, shipEntityId: null as string | null };
    if (isCruiseSegment(seg)) return { isCruise: true, shipEntityId: seg.entityId ?? null };
    if (isCruiseDay(seg)) {
      const parent = seg.parentSegmentId ? segments.find((s) => s.id === seg.parentSegmentId) : null;
      if (parent && isCruiseSegment(parent)) return { isCruise: true, shipEntityId: parent.entityId ?? null };
    }
    return { isCruise: false, shipEntityId: null as string | null };
  }, [segments, selectedSegmentId]);

  // A travel day (or anything filed under one) puts its legs first: the ride
  // share, the airports, the flight.
  const isTravel = useMemo(() => {
    let seg = segments.find((s) => s.id === selectedSegmentId);
    const seen = new Set<string>();
    while (seg && !seen.has(seg.id)) {
      if (isTravelSegment(seg)) return true;
      seen.add(seg.id);
      seg = seg.parentSegmentId ? segments.find((s) => s.id === seg!.parentSegmentId) : undefined;
    }
    return false;
  }, [segments, selectedSegmentId]);

  const [travelEntities, setTravelEntities] = useState<(Entity & { id: string })[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!isTravel) { setTravelEntities([]); return; }
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "entities"), where("type", "in", storedEntityTypes(["TRANSPORTATION", "AIRPORT"]))));
        if (cancelled) return;
        // Ways of getting about first, then the airports, each by name.
        const rank = (t: EntityType) => (t === "AIRPORT" ? 1 : 0);
        const list = snap.docs
          .map((d) => withCurrentType({ id: d.id, ...(d.data() as Entity) }))
          .sort((a, b) => rank(a.type) - rank(b.type) || a.displayName.localeCompare(b.displayName));
        setTravelEntities(list);
      } catch {
        if (!cancelled) setTravelEntities([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isTravel]);
  const activeTravelEntities = useMemo(
    () => partitionByActive(travelEntities, activeDuring).active,
    [travelEntities, activeDuring],
  );
  // How often each has been logged, so the ones actually used come first.
  // Counts need the network; offline the list just falls back to names.
  const [travelUsage, setTravelUsage] = useState<Record<string, number>>({});
  const travelIdsKey = travelEntities.map((e) => e.id).join(",");
  useEffect(() => {
    let cancelled = false;
    const uid = user?.uid;
    if (!uid || !travelIdsKey) return;
    (async () => {
      const counts = await Promise.all(
        travelIdsKey.split(",").map(async (id) => {
          try {
            const snap = await getCountFromServer(
              query(collection(db, "interactions"), where("userId", "==", uid), where("entityId", "==", id)),
            );
            return [id, snap.data().count] as const;
          } catch {
            return [id, 0] as const;
          }
        }),
      );
      if (!cancelled) setTravelUsage(Object.fromEntries(counts));
    })();
    return () => { cancelled = true; };
  }, [user?.uid, travelIdsKey]);
  const travelDayActivities = useMemo(
    () => interactions.filter((i) => (i.segmentId ?? null) === selectedSegmentId),
    [interactions, selectedSegmentId],
  );
  // Every place this trip goes, with its parents (a hotel → Walt Disney
  // World), so transport that belongs to a place only shows on trips there.
  const tripEntityIds = useMemo(
    () =>
      isTravel
        ? [...new Set([...segments.map((s) => s.entityId), ...interactions.map((i) => i.entityId)].filter((id): id is string => !!id))]
        : [],
    [isTravel, segments, interactions],
  );
  const { chains: tripChains } = useEntityHierarchy(tripEntityIds);
  const tripPlaceIds = useMemo(() => new Set(Object.values(tripChains).flat()), [tripChains]);
  // The airport you're at (or close to), from the device location.
  const airportsWithLocation = useMemo(
    () => activeTravelEntities.filter((e) => e.type === "AIRPORT" && e.location),
    [activeTravelEntities],
  );
  const { nearby: nearbyAirports } = useNearbyEntities(airportsWithLocation, {
    enabled: isTravel && airportsWithLocation.length > 0,
    limit: 2,
    maxDistanceMeters: 40_000,
  });
  const nearbyAirportIds = useMemo(() => nearbyAirports.map((n) => n.entity.id), [nearbyAirports]);
  const picks = useMemo(
    () =>
      travelPicks(activeTravelEntities, {
        usage: travelUsage,
        dayActivities: travelDayActivities as { entityId?: string | null; customResponses?: Record<string, unknown> | null }[],
        tripPlaceIds,
        nearbyAirportIds,
      }),
    [activeTravelEntities, travelUsage, travelDayActivities, tripPlaceIds, nearbyAirportIds],
  );
  const [showMoreTravel, setShowMoreTravel] = useState(false);

  const seedKey = seedEntityIds.filter(Boolean).join(",");
  useEffect(() => {
    let cancelled = false;
    setLoadingSuggestions(true);

    (async () => {
      const acc: SuggestedEntity[] = [];
      const seen = new Set<string>();
      for (const seed of seedEntityIds) {
        if (!seed) continue;
        let currentId: string | null = seed;
        while (currentId && !seen.has(currentId)) {
          try {
            const snap = await getDoc(doc(db, "entities", currentId));
            if (!snap.exists()) break;
            const data: Entity = withCurrentType(snap.data() as Entity);
            seen.add(currentId);
            acc.push({ id: currentId, displayName: data.displayName, type: data.type, imageUrl: data.imageUrl });
            currentId = data.parentId ?? null;
          } catch {
            break;
          }
        }
      }
      if (!cancelled) {
        setSuggestions(acc);
        setLoadingSuggestions(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey]);

  // Entities saved under the cruise's ship (e.g. its dining rooms, theaters, lounges).
  useEffect(() => {
    let cancelled = false;
    if (!shipEntityId) { setShipEntities([]); return; }
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "entities"), where("parentId", "==", shipEntityId)));
        if (cancelled) return;
        const list = snap.docs
          .map((d) => withCurrentType({ id: d.id, ...(d.data() as Entity) }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName));
        setShipEntities(list);
      } catch {
        if (!cancelled) setShipEntities([]);
      }
    })();
    return () => { cancelled = true; };
  }, [shipEntityId]);

  // A dining room the line has since closed shouldn't be offered on a sailing
  // that postdates it — same rule as everywhere else, same window.
  const activeShipEntities = useMemo(
    () => partitionByActive(shipEntities, activeDuring).active,
    [shipEntities, activeDuring],
  );

  /** `direct` logs at the place itself even when it holds others: on a travel
   *  day, tapping an airport means "I'm at the airport", not "show me its
   *  restaurants". */
  const handlePick = async (entityId: string, type: EntityType, direct = false) => {
    if (!user || creating) return;
    if (!direct && isContainerEntityType(type)) {
      navigate(
        `/entities/${entityId}${buildTripContextSearch(tripId, selectedSegmentId, selectedParentId)}`,
      );
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const id = await createInteractionAt({
        userId: user.uid,
        tripId,
        segmentId: selectedSegmentId,
        parentInteractionId: selectedParentId,
        entityId,
        name: null,
      });
      navigate(`/trips/${tripId}/interactions/${id}`);
    } catch {
      setError("Failed to create activity. Please try again.");
      setCreating(false);
    }
  };

  /**
   * One tap from the best guess: log it with its first step taken now, close
   * the sheet and let the toast offer Undo, rather than opening the activity
   * to ask. A park or hotel is logged at itself, not browsed into.
   */
  const handleLogGuess = (entity: Entity & { id: string }, first: EventOption) => {
    if (!user || creating) return;
    setCreating(true);
    setError(null);
    createInteractionAt({
      userId: user.uid,
      tripId,
      segmentId: selectedSegmentId,
      parentInteractionId: selectedParentId,
      entityId: entity.id,
      name: null,
      events: [
        {
          eventType: first.eventType,
          eventTimestamp: Timestamp.now(),
          notes: null,
          customLabel: first.displayName,
          timezone: deviceZone() ?? null,
        },
      ],
    })
      .then((id) => {
        if (closeSheet && announceLogged) {
          announceLogged({ interactionId: id, tripId, label: `${first.displayName} · ${entity.displayName}` });
          closeSheet();
        } else {
          navigate(`/trips/${tripId}/interactions/${id}`);
        }
      })
      .catch(() => {
        setError("Failed to create activity. Please try again.");
        setCreating(false);
      });
  };

  /** Enter the custom flow, optionally carrying a name typed into search. */
  const startCustom = (name = "") => {
    setCustomName(name);
    setCustomType(null);
    setMode("custom");
  };

  const leaveCustom = () => {
    setMode("pick");
    setCustomType(null);
  };

  const handlePickActivityType = async (t: typeof CRUISE_ACTIVITY_TYPES[number]) => {
    if (t.key === "custom") {
      startCustom();
      return;
    }
    if (!user || creating) return;
    setCreating(true);
    setError(null);
    try {
      const id = await createInteractionAt({
        userId: user.uid,
        tripId,
        segmentId: selectedSegmentId,
        parentInteractionId: selectedParentId,
        entityId: null,
        name: t.defaultName,
        customResponses: {
          activityType: t.key,
          ...(t.entityType ? { entityType: t.entityType } : {}),
        },
      });
      navigate(`/trips/${tripId}/interactions/${id}`);
    } catch {
      setError("Failed to create activity. Please try again.");
      setCreating(false);
    }
  };

  const handleCreateCustom = async () => {
    if (!user || creating) return;
    const trimmed = customName.trim();
    if (!trimmed) return;
    setCreating(true);
    setError(null);
    try {
      const cr: Record<string, unknown> = {};
      if (customType) cr.entityType = customType;
      if (customLocation.trim()) cr.location = customLocation.trim();
      const id = await createInteractionAt({
        userId: user.uid,
        tripId,
        segmentId: selectedSegmentId,
        parentInteractionId: selectedParentId,
        entityId: null,
        name: trimmed,
        customResponses: Object.keys(cr).length > 0 ? cr : undefined,
      });
      navigate(`/trips/${tripId}/interactions/${id}`);
    } catch {
      setError("Failed to create activity. Please try again.");
      setCreating(false);
    }
  };

  const submitOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCreateCustom();
    }
  };

  const searching = search.trim().length > 0;
  const hasContext = segments.length > 0 || parentCandidates.length > 0 || !!lockedParent;

  // ===== Where it files: one line, tap to change =====
  const contextSummary = hasContext && (
    <div className="log-activity-context">
      {contextOpen && !parentLocked ? (
        <div className="log-activity-context-editor">
          {segments.length > 0 && (
            <label className="log-activity-context-field">
              <span className="log-activity-section-label">Under</span>
              <select
                className="form-input"
                value={selectedSegmentId || ""}
                onChange={(e) => {
                  userPickedSegment.current = true;
                  setSelectedSegmentId(e.target.value || null);
                }}
                disabled={creating}
                aria-label="Segment"
              >
                <option value="">No segment</option>
                {buildNestedSegmentOptions(segments).map(({ id, label }) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </label>
          )}

          {/* Inside a place you already logged — the park you checked into, the
              airport you're waiting in. Pre-selected when you're plainly still
              somewhere; always offered, so it's a suggestion and not a filing
              decision made behind your back. */}
          {parentCandidates.length > 0 && (
            <label className="log-activity-context-field">
              <span className="log-activity-section-label">Inside</span>
              <select
                className="form-input"
                value={selectedParentId || ""}
                onChange={(e) => {
                  userPickedParent.current = true;
                  setSelectedParentId(e.target.value || null);
                }}
                disabled={creating}
                aria-label="Logged inside"
              >
                <option value="">Not inside anything</option>
                {parentCandidates.map((a) => (
                  <option key={a.id} value={a.id}>{activityLabel(a)}</option>
                ))}
              </select>
            </label>
          )}

          <button
            type="button"
            className="btn btn-secondary btn-small log-activity-context-done"
            onClick={() => setContextOpen(false)}
          >
            Done
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="log-activity-context-line"
          onClick={() => setContextOpen(true)}
          // A place holds its activities in its own segment; changing either
          // here would file the child away from its parent.
          disabled={parentLocked || creating}
          aria-label="Change where this activity is logged"
        >
          <span className="log-activity-context-text">
            {parentLocked ? (
              <>
                <span className="log-activity-context-key">Inside</span>{" "}
                <strong>{effectiveParent ? activityLabel(effectiveParent) : "this activity"}</strong>
              </>
            ) : (
              <>
                <span className="log-activity-context-key">Under</span>{" "}
                <strong>{selectedSegment?.name ?? "No segment"}</strong>
                {effectiveParent && (
                  <>
                    {" "}
                    <span className="log-activity-context-key">· inside</span>{" "}
                    <strong>{activityLabel(effectiveParent)}</strong>
                  </>
                )}
              </>
            )}
          </span>
          {!parentLocked && <span className="log-activity-context-change">Change</span>}
        </button>
      )}
    </div>
  );

  // ===== Custom flow: type → name → create =====
  if (mode === "custom") {
    const onTypeStep = customType === null;
    return (
      <div className="log-activity-picker">
        {error && <div className="form-error">{error}</div>}
        {contextSummary}

        <div className="log-activity-flow-header">
          <button
            type="button"
            className="log-activity-flow-back"
            onClick={() => (onTypeStep ? leaveCustom() : setCustomType(null))}
            disabled={creating}
          >
            ‹ Back
          </button>
          <h3 className="log-activity-flow-title">
            {onTypeStep ? "What kind of activity?" : "Name it"}
          </h3>
        </div>

        {onTypeStep ? (
          <div className="log-activity-quick-grid" role="group" aria-label="Custom activity type">
            {CUSTOM_INTERACTION_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                className="log-activity-quick-btn"
                onClick={() => setCustomType(t)}
                disabled={creating}
              >
                <span className="log-activity-quick-icon" aria-hidden="true"><TypeIcon type={t} tile /></span>
                <span className="log-activity-quick-label">{customTypeTileLabel(t)}</span>
              </button>
            ))}
            <button
              type="button"
              className="log-activity-quick-btn"
              onClick={() => setCustomType("")}
              disabled={creating}
            >
              <span className="log-activity-quick-icon" aria-hidden="true"><TypeIcon type="CUSTOM" tile /></span>
              <span className="log-activity-quick-label">Just a name</span>
            </button>
          </div>
        ) : (
          <div className="log-activity-custom-form">
            <button
              type="button"
              className="log-activity-type-chip"
              onClick={() => setCustomType(null)}
              disabled={creating}
              title="Change type"
            >
              <TypeIcon type={customType || "CUSTOM"} />{" "}
              {customType ? entityTypeLabel(customType) : "No type"}
              <span className="log-activity-type-chip-change">change</span>
            </button>
            <input
              id="log-activity-custom-name"
              type="text"
              className="form-input"
              placeholder={customType ? `${entityTypeLabel(customType)} name` : "Activity name"}
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              onKeyDown={submitOnEnter}
              disabled={creating}
              autoFocus
              aria-label="Activity name"
            />
            <input
              type="text"
              className="form-input"
              placeholder="Location (optional)"
              value={customLocation}
              onChange={(e) => setCustomLocation(e.target.value)}
              onKeyDown={submitOnEnter}
              disabled={creating}
              aria-label="Location"
            />
            <button
              type="button"
              className="btn btn-block"
              onClick={handleCreateCustom}
              disabled={!customName.trim() || creating}
            >
              {creating ? "Creating…" : "Create"}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ===== Pick flow: search up top, suggestions beneath =====
  return (
    <div className="log-activity-picker">
      {error && <div className="form-error">{error}</div>}
      {contextSummary}

      {bestGuess && !searching && !isCruise && (
        <LogBestGuess
          guess={bestGuess}
          onLog={(first) => handleLogGuess(bestGuess.entity, first)}
          onOpen={() => handlePick(bestGuess.entity.id, bestGuess.entity.type)}
          disabled={creating}
        />
      )}

      <div className="log-activity-search">
        <span className="log-activity-search-icon" aria-hidden="true">🔍</span>
        <input
          type="search"
          className="form-input log-activity-search-input"
          placeholder="Search places…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          // Keeps the field (and the results under it) above the keyboard
          // once the on-screen keyboard has shrunk the visible area.
          onFocus={(e) => e.currentTarget.scrollIntoView({ block: "nearest" })}
          disabled={creating}
          aria-label="Search places"
          autoComplete="off"
          enterKeyHint="search"
        />
        {searching && (
          <button
            type="button"
            className="log-activity-search-clear"
            onClick={() => setSearch("")}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {searching ? (
        <>
          <LogActivitySearch
            query={search}
            onPick={handlePick}
            disabled={creating}
            activeDuring={activeDuring}
          />
          <button
            type="button"
            className="log-activity-custom-cta"
            onClick={() => startCustom(search.trim())}
            disabled={creating}
          >
            <span aria-hidden="true">✏️</span> Log “{search.trim()}” as a custom activity
          </button>
        </>
      ) : (
        <>
          {isCruise && (
            <>
              {activeShipEntities.length > 0 && (
                <section className="log-activity-section">
                  <h3 className="log-activity-section-label">On this ship</h3>
                  <div className="log-activity-quick-grid">
                    {activeShipEntities.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="log-activity-quick-btn"
                        onClick={() => handlePick(s.id, s.type)}
                        disabled={creating}
                      >
                        <span className="log-activity-quick-icon" aria-hidden="true">
                          {s.imageUrl ? (
                            <img src={s.imageUrl} alt="" className="log-activity-quick-img" />
                          ) : (
                            <TypeIcon type={s.type} name={s.displayName} tile />
                          )}
                        </span>
                        <span className="log-activity-quick-label">{s.displayName}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              <section className="log-activity-section">
                <h3 className="log-activity-section-label">Activity type</h3>
                <div className="log-activity-quick-grid">
                  {CRUISE_ACTIVITY_TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className="log-activity-quick-btn"
                      onClick={() => handlePickActivityType(t)}
                      disabled={creating}
                    >
                      <span className="log-activity-quick-icon" aria-hidden="true">{t.icon}</span>
                      <span className="log-activity-quick-label">{t.label}</span>
                    </button>
                  ))}
                </div>
              </section>
            </>
          )}

          {isTravel && activeTravelEntities.length > 0 && (() => {
            const tile = (s: (typeof activeTravelEntities)[number], hint?: string) => (
              <button
                key={s.id}
                type="button"
                className="log-activity-quick-btn"
                onClick={() => handlePick(s.id, s.type, true)}
                disabled={creating}
              >
                <span className="log-activity-quick-icon" aria-hidden="true">
                  {s.imageUrl ? (
                    <img src={s.imageUrl} alt="" className="log-activity-quick-img" />
                  ) : (
                    <TypeIcon type={s.type} name={s.displayName} tile />
                  )}
                </span>
                <span className="log-activity-quick-label">{s.displayName}</span>
                {hint && <span className="log-activity-quick-hint">{hint}</span>}
              </button>
            );
            return (
              <section className="log-activity-section">
                <h3 className="log-activity-section-label">Getting there</h3>
                <div className="log-activity-quick-grid">
                  {picks.fromFlights.map((s) => tile(s, "On your flight"))}
                  {picks.nearbyAirports.map((s) => tile(s, "Near you"))}
                  {picks.transport.map((s) => tile(s))}
                </div>
                {picks.more.length > 0 && (
                  showMoreTravel ? (
                    <div className="log-activity-quick-grid log-activity-more-grid">{picks.more.map((s) => tile(s))}</div>
                  ) : (
                    <button
                      type="button"
                      className="log-activity-more-btn"
                      onClick={() => setShowMoreTravel(true)}
                    >
                      More ({picks.more.length})
                    </button>
                  )
                )}
              </section>
            );
          })()}

          {!isCruise && (
            <section className="log-activity-section">
              <h3 className="log-activity-section-label">Recent</h3>
              <div className="nearby-slot">
                {loadingSuggestions ? (
                  <NearbySkeleton label="Loading recent places…" />
                ) : suggestions.length === 0 && !recentBack ? (
                  <p className="nearby-slot-msg text-muted">No recent locations yet.</p>
                ) : (
                  <div className="nearby-strip">
                    {recentBack && (
                      <button
                        type="button"
                        className="nearby-card nearby-card-back"
                        onClick={handleBackAtHotel}
                        disabled={creating}
                        aria-label={`Back at ${returnHotelName}`}
                      >
                        <span className="nearby-card-back-tag">Back</span>
                        <span className="nearby-card-emoji">
                          {returnHotel?.imageUrl ? (
                            <img src={returnHotel.imageUrl} alt="" />
                          ) : (
                            <TypeIcon type="HOTEL" name={returnHotelName} tile />
                          )}
                        </span>
                        <span className="nearby-card-name" title={returnHotelName}>
                          {returnHotelName}
                        </span>
                      </button>
                    )}
                    {suggestions.filter((s) => s.id !== recentBack).map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="nearby-card"
                        onClick={() => handlePick(s.id, s.type)}
                        disabled={creating}
                      >
                        <span className="nearby-card-emoji">
                          {s.imageUrl ? (
                            <img src={s.imageUrl} alt="" />
                          ) : (
                            <TypeIcon type={s.type} name={s.displayName} tile />
                          )}
                        </span>
                        <span className="nearby-card-name" title={s.displayName}>{s.displayName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          <section className="log-activity-section">
            <NearbyEntities
              onPick={handlePick}
              disabled={creating}
              activeDuring={activeDuring}
              returnTo={nearbyReturn}
              onReturnNear={setReturnNear}
              onNearby={setNearbyList}
            />
          </section>

          <div className="log-activity-footer">
            <Link
              to={`/entities${buildTripContextSearch(tripId, selectedSegmentId, selectedParentId)}`}
              className="log-activity-link"
            >
              All Destinations →
            </Link>
            {!isCruise && (
              <button
                type="button"
                className="log-activity-custom-cta"
                onClick={() => startCustom()}
                disabled={creating}
              >
                <TypeIcon type="CUSTOM" /> Custom activity
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
